import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import ConferenceGuarantorsTemplate from '@components/ConferenceGuarantors/ConferenceGuarantors';
import useTranslations from '@hooks/useTranslation';
import Cookies from 'js-cookie';
import moment from 'moment';
import 'moment/locale/sk';
import { ACCESS_TOKEN_KEY_GUEST, getApiURL } from '../../constants';
import { COMPONENT_TYPE, IColors, IConferenceItem, IEventDate } from '../../interfaces/Conference';
import {
    ButtonPartner,
    ButtonTicket,
    Buttons,
    Date,
    Image,
    Label,
    Lecturer,
    LecturerName,
    LecturerSurname,
    Lecturers,
    LecturersList,
    LecturersTitle,
    Place,
    Title,
    Wrapper,
} from './ConferenceHeader.elements';
import { RootState } from '../../store';

interface ConferenceProps {
    colors?: IColors;
    conf?: IConferenceItem;
    setActiveSection?: Function;
}

const ConferenceHeaderTemplate: FC<ConferenceProps> = ({ colors, conf, setActiveSection }) => {
    const { t } = useTranslations();

    const isLoggedInState = useSelector((state: RootState) => state.ui.isLoggedIn);

    const [eventDate, setEventDate] = useState(null);
    const [timeToShowOnline, setTimeToShowOnline] = useState(false);
    const [timeToShowRecord, setTimeToShowRecord] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const [venues, setVenues] = useState([]);

    const inHeroLectors = conf?.lectors.filter((item) => item?.inHero);
    const garantPartners = conf?.partners.filter((item) => item.isGarant);
    const patronPartners = conf?.partners.filter((item) => item.isPatron);

    const isSetTicket = conf?.conference?.components.filter((item) => item.type === 4);
    const isSetVideo = conf?.conference?.components.filter((item) => item.type === 10);
    const isSetOnlineSection = conf?.conference?.components.filter((item) => item.type === 18);

    useEffect(() => {
        setIsLoggedIn(isLoggedInState);
    }, [isLoggedInState]);

    useEffect(() => {
        if (conf?.dates) {
            setEventDate(conf?.dates);
        }
    }, []);

    useEffect(() => {
        if (eventDate) {
            const fromDates = eventDate.map((item: IEventDate) => item.date.from);
            const min = fromDates.reduce(function (a, b) {
                return a < b ? a : b;
            });
            const toDates = eventDate.map((item: IEventDate) => item.date.to);
            const max = toDates.reduce(function (a, b) {
                return a > b ? a : b;
            });

            const videos = [];
            for (let x = 0; x < eventDate.length; x++) {
                for (let z = 0; z < eventDate[x].videos.length; z++) {
                    videos.push(eventDate[x]?.videos[z]);
                }
            }

            if (moment() >= moment(min).subtract(1, 'h') && moment() <= moment(max)) {
                setTimeToShowOnline(true);
            } else {
                setTimeToShowOnline(false);
            }

            if (moment() > moment(max) && videos.length > 0 && moment() <= moment(max).subtract(-30, 'd')) {
                setTimeToShowRecord(true);
            } else {
                setTimeToShowRecord(false);
            }

            for (let x = 0; x < eventDate.length; x++) {
                for (let z = 0; z < eventDate[x].venues.length; z++) {
                    venues.push(eventDate[x]?.venues[z]);
                }
            }
            const venuesUnique = venues.filter(
                (value, index, self) => self.map((x) => x._key).indexOf(value._key) == index
            );
            setVenues(venuesUnique);
        }
    }, [eventDate]);

    const getEventDateForConference = () => {
        const fromDates = eventDate.map((item: IEventDate) => item.date.from);
        const min = fromDates.reduce(function (a, b) {
            return a < b ? a : b;
        });
        const minStr = moment(min).format('D. M. ');

        const toDates = eventDate.map((item: IEventDate) => item.date.to);
        const max = toDates.reduce(function (a, b) {
            return a > b ? a : b;
        });
        const maxStr = moment(max).format('D. M. YYYY');

        const nonComplet = `${moment(min).format('MMMM')} ${moment(min).format('YYYY')}`;

        return max ? (
            <Date>
                {moment(min).format('D. M. YYYY') === moment(max).format('D. M. YYYY')
                    ? `${moment(max).format('D. M. YYYY')}`
                    : `${moment(min).format('D. M. ')} – ${moment(max).format('D. M. YYYY')}`}
            </Date>
        ) : (
            <Date>{nonComplet}</Date>
        );
    };

    const handleMenuClick = (id: string) => {
        const top = document.getElementById(id).offsetTop;
        setActiveSection(id);
        return window.scrollTo({ top: top - 80, behavior: 'smooth' });
    };

    const handleOnlineClick = (token: string) => {
        window.open(`https://konferencie.pp.sk/login?auth=${token}`, '_blank');
    };

    return (
        <>
            <Wrapper id={`confSection${COMPONENT_TYPE.HERO}`}>
                <Image src={`${getApiURL()}/media/thumbnail/conference/${conf?.conference?.photo}?size=1024`} />
                <Label>{conf?.conference?.sub_title}</Label>
                <Title onClick={getEventDateForConference}>{conf?.conference?.title}</Title>

                {eventDate && getEventDateForConference()}

                {venues.length > 0 && (
                    <Place>
                        {venues[0]?.city}, {venues[0]?.name}
                    </Place>
                )}

                <Buttons>
                    {((isSetTicket.length > 0 && !timeToShowOnline) ||
                        (isSetTicket.length > 0 && !conf?.token && timeToShowOnline)) && (
                            <ButtonTicket colors={colors} onClick={() => handleMenuClick(`confSection4`)}>
                                {t('conference.general.tickets')}
                            </ButtonTicket>
                        )}

                    {isSetOnlineSection.length == 1 && conf?.token && timeToShowOnline && (isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && (
                        <ButtonTicket colors={colors} onClick={() => handleMenuClick(`confSection18`)}>
                            {t('conference.general.onlineTransmission')}
                        </ButtonTicket>
                    )}

                    {isSetVideo && !timeToShowOnline && timeToShowRecord && isLoggedIn && (
                        <ButtonTicket colors={colors} onClick={() => handleMenuClick(`confSection10`)}>
                            {t('conference.general.Record')}
                        </ButtonTicket>
                    )}

                    {conf?.conference?.showPartnership && (
                        <ButtonPartner colors={colors} onClick={() => handleMenuClick(`confSection13`)}>
                            {t('conference.component.partnership.buttontitle')}
                        </ButtonPartner>
                    )}
                </Buttons>

                <Lecturers>
                    {inHeroLectors.length > 0 && (
                        <>
                            <LecturersTitle>{t('conference.component.lecturers.title')}:</LecturersTitle>
                            <LecturersList>
                                {inHeroLectors.map((item, index) => (
                                    <Lecturer key={`lectureName${index}`}>
                                        <LecturerName>
                                            {item.name}
                                        </LecturerName>{' '}
                                        <LecturerSurname>{item.surname}</LecturerSurname>

                                        {index + 1 !== inHeroLectors.length && <LecturerName>{','}&nbsp;</LecturerName>}

                                    </Lecturer>
                                ))}
                                {inHeroLectors.length < conf?.lectors.length && (
                                    <LecturerSurname>&nbsp;a další</LecturerSurname>
                                )}
                            </LecturersList>
                        </>
                    )}
                </Lecturers>
            </Wrapper>
            <ConferenceGuarantorsTemplate
                garantPartners={garantPartners}
                patronPartners={patronPartners}
                colors={colors}
                conf={conf}
            />
        </>
    );
};

export default ConferenceHeaderTemplate;
